import { useState } from "react";
import type { StudioAuthClient } from "./auth-api.js";

export function StudioLoginPanel({
  auth,
  onAuthenticated,
}: {
  readonly auth: StudioAuthClient;
  readonly onAuthenticated: () => void;
}) {
  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");
  const [step, setStep] = useState<"email" | "verify">("email");
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const requestLink = async () => {
    setLoading(true);
    setError(null);
    setMessage(null);
    try {
      const result = await auth.requestLogin(email.trim());
      if (result.devMagicLinkToken) {
        setToken(result.devMagicLinkToken);
        setStep("verify");
        setMessage("Dev magic link token loaded. Verify to sign in.");
      } else {
        setStep("verify");
        setMessage("If your account exists, check your email for a sign-in link.");
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "login_request_failed");
    } finally {
      setLoading(false);
    }
  };

  const verify = async () => {
    setLoading(true);
    setError(null);
    try {
      await auth.verifyLogin(token.trim());
      onAuthenticated();
    } catch (e) {
      setError(e instanceof Error ? e.message : "login_verify_failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="studio-login-panel">
      <h1>Agency sign in</h1>
      <p className="subcopy">Magic-link login for Studio command center access.</p>
      {step === "email" ? (
        <>
          <label htmlFor="studio-login-email">Work email</label>
          <input
            id="studio-login-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
          />
          <button type="button" disabled={loading || email.trim().length === 0} onClick={() => void requestLink()}>
            {loading ? "Sending…" : "Send magic link"}
          </button>
        </>
      ) : (
        <>
          <label htmlFor="studio-login-token">Magic link token</label>
          <input
            id="studio-login-token"
            type="text"
            value={token}
            onChange={(e) => setToken(e.target.value)}
          />
          <button type="button" disabled={loading || token.trim().length === 0} onClick={() => void verify()}>
            {loading ? "Verifying…" : "Verify and sign in"}
          </button>
          <button type="button" className="link-button" onClick={() => setStep("email")}>
            Use a different email
          </button>
        </>
      )}
      {message ? <p className="success" role="status">{message}</p> : null}
      {error ? <p className="error" role="alert">{error}</p> : null}
    </div>
  );
}
